import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const BASE = "/images/";

const galleryImages = [
  {
    src: BASE + "neu/Pirate-Bowling-6.jpg",
    alt: "Bowling-Bahnen Frontansicht mit Piraten-Sonnenuntergang-Mural",
    caption: "Bowling-Bahnen",
    testId: "gallery-lanes",
  },
  {
    src: BASE + "neu/Pirate-Bowling-15.jpg",
    alt: "Billard-Bereich mit Tageslicht im Pirate Bowling Center",
    caption: "Billard-Bereich",
    testId: "gallery-billiards",
  },
  {
    src: BASE + "neu/Pirate-Bowling-23.jpg",
    alt: "Piraten-Bar mit Schiffsbar im Pirate Bowling Center",
    caption: "Die Piraten-Bar",
    testId: "gallery-bar",
  },
  {
    src: BASE + "neu/Pirate-Bowling-25.jpg",
    alt: "Gemütlicher Sitzbereich mit tropischem Wandgemälde",
    caption: "Lounge & Ambiente",
    testId: "gallery-lounge",
  },
  {
    src: BASE + "neu/Pirate-Bowling-24.jpg",
    alt: "Bowlingbahnen aus Spielerperspektive mit Anzeigetafeln",
    caption: "Game On!",
    testId: "gallery-game-on",
  },
  {
    src: BASE + "neu/Pirate-Bowling-21.jpg",
    alt: "Rustikaler Sitzbereich mit Piraten-Kunstwerken",
    caption: "Piraten-Atmosphäre",
    testId: "gallery-atmosphere",
  },
];

/* ── Lightbox Modal ── */
function Lightbox({ images, index, onClose, onNav }) {
  const img = images[index];

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    },
    [onClose, onNav]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [handleKey]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-navy-deep"
      onClick={onClose}
      role="dialog"
      aria-label="Galerie Lightbox"
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-cream/60 hover:text-gold transition-colors z-10 cursor-pointer"
        aria-label="Schließen"
      >
        <X className="w-7 h-7" />
      </button>

      {/* Prev */}
      <button
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-cream/40 hover:text-gold transition-colors z-10 cursor-pointer"
        aria-label="Vorheriges Bild"
      >
        <ChevronLeft className="w-9 h-9" />
      </button>

      {/* Next */}
      <button
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-cream/40 hover:text-gold transition-colors z-10 cursor-pointer"
        aria-label="Nächstes Bild"
      >
        <ChevronRight className="w-9 h-9" />
      </button>

      {/* Image */}
      <AnimatePresence mode="wait">
        <motion.div
          key={img.src}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.92 }}
          transition={{ duration: 0.25 }}
          className="max-w-[90vw] max-h-[85vh] relative"
          onClick={(e) => e.stopPropagation()}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={(e, info) => {
            if (info.offset.x < -80) onNav(1);
            else if (info.offset.x > 80) onNav(-1);
          }}
        >
          <img
            src={img.src}
            alt={img.alt}
            className="max-w-full max-h-[85vh] object-contain rounded-xl pointer-events-none select-none"
            draggable={false}
            loading="lazy"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
            <span className="font-space-grotesk text-sm text-cream bg-navy-deep/80 px-4 py-2 rounded-full border border-gold/20 inline-block">
              {img.caption}
            </span>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Counter */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-space-grotesk text-xs text-cream/40">
        {index + 1} / {images.length}
      </div>
    </motion.div>
  );
}

/* ── Gallery Section ── */
export default function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const navigate = useCallback(
    (dir) => {
      setLightboxIndex((prev) => {
        if (prev === null) return null;
        return (prev + dir + galleryImages.length) % galleryImages.length;
      });
    },
    []
  );

  return (
    <section
      id="gallery"
      className="relative py-28 md:py-36 px-6 md:px-12 lg:px-20 bg-navy"
      data-testid="gallery-section"
    >
      <div className="hidden 2xl:block absolute top-0 left-0 w-80 h-80 bg-gold/8 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="skull-divider mb-6">
            <span className="font-space-grotesk text-xs text-gold tracking-[0.3em] uppercase px-3">Atmosphäre</span>
          </div>
          <h2 className="font-unbounded text-4xl sm:text-5xl lg:text-6xl font-black text-cream leading-tight">
            Erlebe die{" "}
            <span className="gold-gradient-text">Stimmung</span>
          </h2>
          <p className="font-inter text-cream-muted text-sm md:text-base mt-5 max-w-md mx-auto">
            Echte Eindrücke aus unserem Center – Bowling, Bar, Spiele und pure Unterhaltung.
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 auto-rows-[200px] md:auto-rows-[260px]">
          {galleryImages.map((img, i) => (
            <motion.div
              key={img.testId}
              initial={{ opacity: 0, scale: 0.93 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="relative overflow-hidden rounded-xl group cursor-pointer"
              data-testid={img.testId}
              onClick={() => openLightbox(i)}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading={i === 0 ? "eager" : "lazy"}
              />
              {/* Permanent subtle overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
              {/* Caption — always visible on mobile, hover on desktop */}
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-0 opacity-100 md:translate-y-2 md:opacity-0 md:group-hover:opacity-100 md:group-hover:translate-y-0 transition-all duration-300">
                <span className="font-space-grotesk text-xs text-cream bg-navy-deep/80 px-3 py-1.5 rounded-full border border-gold/20 inline-block">
                  {img.caption}
                </span>
              </div>
              {/* Gold frame on hover */}
              <div className="absolute inset-0 rounded-xl border border-gold/0 group-hover:border-gold/30 transition-all duration-400" />
              {/* Zoom hint */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-8 h-8 rounded-full bg-navy-deep/80 border border-gold/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                    <path d="M11 8v6M8 11h6" />
                  </svg>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-10"
        >
          <p className="font-inter text-cream/50 text-xs mb-5 uppercase tracking-widest">Folgt der Crew</p>
          <div className="flex justify-center gap-4">
            <a href="https://www.instagram.com/pirate_bowling_center/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass border border-gold/28 hover:border-gold/40 text-cream-muted hover:text-gold transition-all duration-300 text-xs font-space-grotesk cursor-pointer"
              data-testid="gallery-instagram-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
              Instagram
            </a>
            <a href="https://www.facebook.com/p/Pirate-Bowling-Center-Velburg-100063452118398/?locale=de_DE" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass border border-gold/28 hover:border-gold/40 text-cream-muted hover:text-gold transition-all duration-300 text-xs font-space-grotesk cursor-pointer"
              data-testid="gallery-facebook-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
              Facebook
            </a>
          </div>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            images={galleryImages}
            index={lightboxIndex}
            onClose={closeLightbox}
            onNav={navigate}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
